<template>
  <Page :class="{ toggle: toggleSidebar }">
    <ManageCollectionItemModal
      v-if="isManageCollectionItemModalShowing"
      :collectionType="loadItem.collectionType"
      :itemId="loadItem.itemId"
      :onClose="onCloseManageCollectionItemModal" />
    <FilterSidebar
      class="sidebar"
      v-if="toggleSidebar"
      :onClose="handleToggleSidebar"
      :enableFilterOption="filterOptions" />
    <template v-slot:page-primary v-if="pinnedCollections && pinnedCollections.length > 0">
      <div class="container">
        <CollectionSuggestion
          :collections="pinnedCollections"
          :collectionType="'episode'">
          <template v-slot:collection-header>
            <i class="fas fa-bookmark mr-1" /> ชุดตอนปักหมุด
          </template>
        </CollectionSuggestion>
      </div>
    </template>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="title">ตอน</div>
          </div>
          <div class="actions">
            <router-link :to="'/episodes/import'" class="btn btn-outline-secondary mr-3">
              <i class="fa fa-cloud-download icon-align-left"></i> นำเข้าตอน
            </router-link>
            <router-link :to="'/episodes/create'" class="btn btn-primary">
              <i class="fas fa-plus icon"></i> สร้างตอนใหม่
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <PreloaderSection v-if="loadEpisodesListState.status === 'loading'" />
      <div>
        <!-- render content -->
        <Section v-if="loadEpisodesListState.status === 'success'">
          <template v-slot:section-filter>
            <div class="search-container">
              <input
                class="form-control form-control-search"
                :value="filter.q"
                placeholder="ค้นหาด้วยชื่อตอน"
                @change="onSearch" />
              <div
                class="filter-button ml-2"
                @click="handleToggleSidebar">
                <i class="fa fa-filter icon mr-2" />
                <span class="filter-label">ค้นหาขั้นสูง</span>
              </div>
            </div>
          </template>
          <!-- render content -->
          <template v-slot:section-body>
            <div v-if="!isEmpty()">
              <List>
                <template v-slot:list-header>
                  <div class="list-item-image"></div>
                  <div class="list-item-detail">
                    <div class="list-item list-item-primary list-item-title">
                      ชื่อตอน
                    </div>
                    <div class="list-item">ออกอากาศครั้งแรก</div>
                    <div class="list-item list-item-create-at">สร้างเมื่อ</div>
                    <div class="list-item list-item-status">สถานะ</div>
                    <div class="list-item list-item-action"></div>
                  </div>
                </template>
                <template v-slot:list-body>
                  <div
                    class="list-item-container no-underline"
                    v-for="(item, itemIndex) in episodes"
                    :key="item.id"
                    @click.stop="goToPageDetail(`/episodes/${item.id}`, itemIndex, $event)">
                    <div class="list-item-image">
                      <ImageView
                        :source="item.coverImage"
                        imageSize="cover160w90h" />
                    </div>
                    <div class="list-item-detail">
                      <div class="list-item list-item-primary list-item-title">
                        <div class="subtitle">
                          {{ getProgramName(item.program) }}
                        </div>
                        <div class="title">{{ item.title }}</div>
                      </div>
                      <div class="list-item">
                        {{item.firstAiredAt ? formatDateTime(item.firstAiredAt) : '-'}}
                      </div>
                      <div class="list-item list-item-create-at">
                        {{ formatDateTime(item.createdAt) }}
                      </div>
                      <div class="list-item list-item-status">
                        <div class="badge badge-status" :class="item.status">
                          {{ item.status }}
                        </div>
                      </div>
                      <div class="list-item list-item-action">
                        <div class="action-container">
                          <button
                            class="btn btn-light btn-action"
                            @click.stop="onToggleManageCollectionItemModal(item.id)">
                            <i class="far fa-plus-square icon" />
                          </button>
                          <router-link
                            class="btn btn-light btn-action"
                            :to="`/episodes/${item.id}`">
                            <i class="fas fa-pencil-alt"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </List>
              <div class="pagination-container" v-if="isShowPagination()">
                <Pagination :lastPage="lastPage" />
              </div>
            </div>
            <!-- empty list -->
            <EmptyMessage v-if="isEmpty()">
              <h3>ไม่มีข้อมูล</h3>
              <router-link
                class="btn btn-light btn-lg mt-4"
                :to="`/episodes/create`">
                สร้างตอน
              </router-link>
            </EmptyMessage>
            <EmptyMessage v-if="isEmptySearch()">
              <h3>ไม่พบตอนที่ค้นหา</h3>
              <button
                type="button"
                class="btn btn-light btn-lg mt-4"
                @click="clearSearch">
                ล้างการค้นหา
              </button>
            </EmptyMessage>
          </template>
        </Section>
        <!-- error from api -->
        <ErrorMessageEpisodeContainer
          v-if="loadEpisodesListState.status === 'error'"
          :error="loadEpisodesListState.error" />
      </div>
    </div>
  </Page>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import { loadCollections } from '@/collection/api';
import dateFormetter from '@/utils/dateFormatter';

import Page from '@/layouts/Page.vue';
import Pagination from '@/components/Pagination.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';
import FilterSidebar from '@/components/FilterSidebar.vue';
import ManageCollectionItemModal from '@/collection/ManageCollectionItemModal.vue';
import CollectionSuggestion from '@/collection/CollectionSuggestion.vue';
import ImageView from '@/image/ImageView.vue';

// import { loadEpisodes } from './api';
import ErrorMessageEpisodeContainer from './ErrorMessageEpisodeContainer.vue';

export default {
  mixins: [dateFormetter],
  components: {
    Page,
    Pagination,
    Section,
    List,
    EmptyMessage,
    ErrorMessageEpisodeContainer,
    PreloaderSection,
    CollectionSuggestion,
    FilterSidebar,
    ImageView,
    ManageCollectionItemModal,
  },
  data() {
    return {
      pinnedCollections: null,
      toggleSidebar: false,
      filterOptions: ['program', 'contentCategories', 'status', 'age', 'subject'],
      isManageCollectionItemModalShowing: false,
      loadItem: {
        itemId: '',
        collectionType: 'episode',
      },
    };
  },
  computed: {
    ...mapState({
      episodes: (state) => state.episodesList.episodes,
      loadEpisodesListState: (state) => state.episodesList.loadEpisodesListState,
      lastPage: (state) => state.episodesList.lastPage,
      contentFilter: (state) => state.filter.filter,
    }),
    filter() {
      return this.$route.query;
    },
  },
  watch: {
    filter(newValue) {
      this.loadEpisodes(newValue);
    },
  },
  created() {
    this.loadEpisodes(this.filter);
    this.loadCollections();
    if (this.filter.q) {
      this.search = this.filter.q;
    }
  },
  methods: {
    async loadEpisodes(filter) {
      this.$store.dispatch('loadEpisodesList', filter);
    },
    async loadCollections() {
      const filter = {
        isPinned: true,
        type: 'episode',
      };
      const response = await loadCollections(filter);
      this.pinnedCollections = response.data.data;
    },
    isEmptySearch() {
      return _.isEmpty(this.episodes) && !_.every(this.filter, _.isEmpty);
    },
    isEmpty() {
      return _.isEmpty(this.episodes) && _.every(this.filter, _.isEmpty);
    },
    onSearch(event) {
      this.$store.dispatch('setFilterState', { q: event.target.value });
      if (this.contentFilter.q) {
        this.$router.push({ name: 'episodeList', query: { q: this.contentFilter.q, ...this.contentFilter } });
      } else {
        this.$router.push({ name: 'episodeList', query: { ...this.contentFilter } });
      }
    },
    handleToggleSidebar() {
      this.toggleSidebar = !this.toggleSidebar;
    },
    clearSearch() {
      this.$store.dispatch('clearFilter');
      this.$router.push({ query: null });
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
    onToggleManageCollectionItemModal(itemId) {
      this.loadItem.itemId = itemId;
      this.isManageCollectionItemModalShowing = !this
        .isManageCollectionItemModalShowing;
    },
    onCloseManageCollectionItemModal() {
      this.isManageCollectionItemModalShowing = false;
    },
    getProgramName(program) {
      return _.get(program, 'title', null);
    },
  },
  beforeDestroy() {
    this.$store.dispatch('clearFilter');
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/layouts/page.scss';
@import './src/assets/scss/themes/components/form.scss';

.toggle {
  transition: all 0.3s ease-out;
  margin-right: 250px;
}

@media (max-width: 575.98px) {
  .toggle {
    margin-right: 0px;
  }

}
@media (min-width: 576px) and (max-width: 1023.98px) {
  .toggle {
    margin-right: 0px;
  }

  .list-item-container {
    .list-item-detail {
      .list-item {
        &.list-item-status {
          display: none;
        }
        &.list-item-create-at {
          display: none;
        }
        &.list-item-action {
          .action-container {
            .btn-external {
              display: none;
            }
          }
        }
      }
    }
  }
}
</style>
